/**
 * Pure Decision-1 resolver for Pro tracking destination.
 * No I/O, no React — eligible list + optional remembered/context ids in,
 * status (+ workspace when selected) out.
 */

export interface EligibleWorkspace {
  id: string;
  name: string;
  organization_name?: string;
  access_level?: string;
}

export type ResolveTrackingWorkspaceInput = {
  eligible: EligibleWorkspace[];
  rememberedId?: string | null;
  contextId?: string | null;
};

export type ResolveTrackingWorkspaceResult =
  | { status: 'selected'; workspace: EligibleWorkspace }
  | { status: 'needs-choice' }
  | { status: 'none' }
  | { status: 'context-ineligible' };

export function resolveTrackingWorkspace({
  eligible,
  rememberedId,
  contextId,
}: ResolveTrackingWorkspaceInput): ResolveTrackingWorkspaceResult {
  const findById = (id: string) => eligible.find((w) => w.id === id);

  if (contextId) {
    const fromContext = findById(contextId);
    if (fromContext) {
      return { status: 'selected', workspace: fromContext };
    }
    // Context set but not eligible — do not fall through to remembered.
    return { status: 'context-ineligible' };
  }

  if (rememberedId) {
    const fromRemembered = findById(rememberedId);
    if (fromRemembered) {
      return { status: 'selected', workspace: fromRemembered };
    }
    // Stale remembered — fall through.
  }

  if (eligible.length === 1) {
    return { status: 'selected', workspace: eligible[0] };
  }

  if (eligible.length === 0) {
    return { status: 'none' };
  }

  return { status: 'needs-choice' };
}
